<template>
  <div class="page-main">
    <div class="page-header" v-if="currentPage === 'self'">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">{{this.$route.params.namespace}}</li>
        <li class="breadcrumb-item active">{{ $t("member.labels.members") }}</li>
      </ol>
      <h1 class="page-title">{{ $t("member.labels.createMember") }}</h1>
    </div>
    <div class="page-header" v-else>
      <ol class="breadcrumb">
        <li class="breadcrumb-item">{{currentPage === 'admin' ? 'Admin' : 'Reseller'}}</li>
        <li class="breadcrumb-item">
          <router-link v-bind:to="`/${$route.params.namespace}/${currentPage}/teams`">{{ $t("action.manageClientTeam") }}</router-link>
        </li>
        <li class="breadcrumb-item active">
          <router-link v-bind:to="`/${$route.params.namespace}/${currentPage}/teams/${$route.params.subnamespace}/edit`">{{ $route.params.subnamespace }} </router-link>
        </li>
      </ol>
      <h1 class="page-title">{{ $t("member.labels.createMember") }}</h1>
    </div>
    <div class="page-content">
      <div class="panel">
        <div class="panel-body panel-form">
          <form v-on:submit.prevent="onSubmit" autocomplete="off">
            <div class="row">
              <div class="col-md-6">
                <alert v-show="showAlert"
                  alertClass="m-b-25 alert-dismissible"
                  v-bind:type="`${ (successResponse) ? 'success' : 'danger' }`"
                  v-bind:iconClass="`${ (successResponse) ? 'md-check' : 'md-close' }`">
                  <span v-if="successResponse">{{successResponse}}</span>
                  <span v-else-if="errorResponse">{{errorResponse}}</span>
                </alert>
                <div class="form-section">
                  <form-group v-bind:error="formErrors.first('name')" autocomplete="false">
                    <label for="name">{{ $t("account.labels.displayName") }}</label>
                    <input type="text" class="form-control input-lg" :placeholder="$t('account.labels.displayName')" v-model="user.name">
                  </form-group>
                  <form-group v-bind:error="formErrors.first('email')">
                    <label for="email">{{ $t("account.labels.email") }}</label>
                    <input type="email" class="form-control input-lg"  :placeholder="$t('account.labels.email')" v-model="user.email" autocomplete="false">
                  </form-group>
                  <form-group v-bind:error="formErrors.first('password')">
                    <label for="password">{{ $t("account.labels.password") }}</label>
                    <input type="password" class="form-control input-lg" :placeholder="$t('account.labels.password')" v-model="user.password">
                  </form-group>
                  <form-group v-bind:error="formErrors.first('role')">
                    <label for="role">{{ $t("account.labels.role") }}</label>
                    <div class="radio-custom radio-primary">
                      <input type="radio" value="manager" v-model="user.role">
                      <label for="manager"> Manager</label>
                    </div>
                    <div class="radio-custom radio-primary" v-if="showUserRole">
                      <input type="radio" value="user" v-model="user.role">
                      <label for="user">User <span class="grey-400">{{ $t("member.role.user.textGuide") }}</span>
                      </label>
                    </div>
                  </form-group>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-4">
                <button type="submit" class="btn btn-success" :disabled="isSubmiting" v-bind:class="{ 'btn-default': isSubmiting }">
                  <div class="loader vertical-align-middle loader-circle loader-form" v-if="isSubmiting"></div> {{ $t("action.create") }}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { Validator } from 'vee-validate';
import { mapGetters } from 'vuex';
import FormGroup from 'components/forms/FormGroup';
import Alert from 'components/Alert';

export default {
  components: {
    FormGroup,
    Alert,
  },
  watch: {
    'user.email': function() {
      if (this.formErrors.has('email')) {
        this.formErrors.remove('email');
      }
    },
    'user.name': function() {
      if (this.formErrors.has('name')) {
        this.formErrors.remove('name');
      }
    },
    'user.password': function() {
      if (this.formErrors.has('password')) {
        this.formErrors.remove('password');
      }
    },
    'user.role': function() {
      if (this.formErrors.has('role')) {
        this.formErrors.remove('role');
      }
    },
  },
  computed: {
    ...mapGetters([
      'currentUser',
    ]),
    showUserRole: function() {
      if (!this.$route.params.subnamespace &&
        (this.currentUser.team.is_superadmin || this.currentUser.team.is_reseller)) {
        this.user.role = 'manager';
        return false;
      } else {
        this.user.role = 'user';
        return true;
      }
    },
    currentPage: function () {
      if (this.$route.params.subnamespace && this.currentUser.team.is_superadmin) {
        this.redirectUrl = `/${this.$route.params.namespace}/admin/teams/${this.$route.params.subnamespace}/edit`;
        return 'admin';
      } else if (this.$route.params.subnamespace && this.currentUser.team.is_reseller) {
        this.redirectUrl = `/${this.$route.params.namespace}/reseller/teams/${this.$route.params.subnamespace}/edit`;
        return 'reseller';
      } else {
        this.redirectUrl = `/${this.$route.params.namespace}/team/members`;
        return 'self';
      }
    },
  },
  data: function () {
    return {
      isSubmiting: false,
      showAlert: false,
      validator: null,
      formErrors: null,
      errorResponse: null,
      successResponse: null,
      redirectUrl: '',
      user: {
        email: null,
        name: null,
        password: null,
        role: '',
      },
    };
  },
  created: function() {
    this.isLoading = true;
    this.validator = new Validator();
    this.validator.attach('email', 'required|email');
    this.validator.attach('name', 'required', { prettyName: 'display name' });
    this.validator.attach('password', 'required');
    this.validator.attach('role', 'required');
    this.validator.attach('password', 'required|min:6');

    this.$set(this, 'formErrors', this.validator.errorBag);
  },
  mounted: function() {
    if (this.showUserRole) {
      this.user.role = 'user';
    } else {
      this.user.role = 'manager';
    }
  },
  methods: {
    onSubmit: function () {
      this.submitForm = false;

      this.validator.validateAll({
        name: this.user.name,
        email: this.user.email,
        password: this.user.password,
        role: this.user.role,
      })
      .then((success) => {
        if (!success) {
          return;
        }

        let namespace = this.$route.params.namespace;

        if (this.$route.params.subnamespace) {
          namespace = this.$route.params.subnamespace;
        }

        this.isSubmiting = true;

        axios.post(`/api/teams/${namespace}/users`, {
          email: this.user.email,
          name: this.user.name,
          password: this.user.password,
          role: this.user.role,
          team_id: this.currentUser.team.id,
        })
        .then(() => {
          this.isSubmiting = false;
          this.successResponse = this.$t('team.response.success');
          this.errorResponse = null;
          this.showAlert = true;

          // back to Member list
          setTimeout(() => {
            this.$router.push(this.redirectUrl);
          }, 2000);
        })
        .catch((error) => {
          this.isSubmiting = false;
          if (error.response.status === 422) {
            this.errorResponse = this.$t('team.response.failed');
            this.formErrors.addAll(error.response.data);
          } else {
            this.errorResponse = this.$t('team.response.defaultFailed');
          }
          this.showAlert = true;
        });
      })
      .catch(() => {
      });
    },
  },
};
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// TeamMemberCreator.vue?9e8e1260